import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Subheading from "../../atoms/typography/Subheading";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Col, Row } from "react-grid-system";
import TextField from "../../atoms/inputs/TextField";
import BodySmall from "../../atoms/typography/BodySmall";
import MultiSelect from "../../atoms/inputs/MultiSelect";
import Divider from "../../atoms/Divider";
import { theme } from "../../../theme";
import dayjs from "dayjs";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import type { SkynetUserModel } from "../../../models/RolesAndPermissionsModels";
import { useUserGroups } from "../../../graphql/hooks/rolesAndPermissions/useUserGroups";
import UserPermissionsList from "../../molecules/users/UserPermissionsList";
import {
  PERMISSION_CATEGORIES,
  type PermissionOverridePreview,
} from "../../../models/permissionLabels";
import { useCreateSkynetUser } from "../../../graphql/hooks/rolesAndPermissions/useCreateSkynetUser";
import { useUpdateSkynetUser } from "../../../graphql/hooks/rolesAndPermissions/useUpdateSkynetUser";
import { useDeleteSkynetUser } from "../../../graphql/hooks/rolesAndPermissions/useDeleteSkynetUser";
import { useAddMembers } from "../../../graphql/hooks/rolesAndPermissions/useAddMembers";
import { useRemoveMember } from "../../../graphql/hooks/rolesAndPermissions/useRemoveMember";
import Alert from "../../molecules/Alert";
import { validateEmail } from "../../../utils/validation";
import DeleteModal from "../../molecules/modals/DeleteModal";

interface Props {
  user?: SkynetUserModel;
  onClose?: () => void;
  onSuccess?: () => void;
  onUserDeleted?: () => void;
}

const ManageUserModalContent: React.FC<Props> = ({
  user,
  onClose,
  onSuccess,
  onUserDeleted,
}) => {
  const { t } = useTranslation();
  const { userGroups } = useUserGroups();

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const originalGroupIds =
    user?.groups?.map((membership) => membership.groupId) ?? [];
  const [selectedGroups, setSelectedGroups] =
    useState<string[]>(originalGroupIds);
  const [amUserId, setAmUserId] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setFirstName(user?.firstName ?? "");
    setLastName(user?.lastName ?? "");
    setEmail(user?.email ?? "");
    setSelectedGroups(
      user?.groups?.map((membership) => membership.groupId) ?? [],
    );
  }, [user]);

  const onCompletedCreateUser = (result?: any) => {
    if (result?.rolesAndPermissions?.createSkynetUser?.success) {
      setErrorMessage(null);
      onSuccess?.();
      onClose?.();
    } else {
      setErrorMessage(t("users.createUserFailedMessage"));
    }
  };

  const onErrorCreateUser = (error?: any) => {
    setErrorMessage(error?.message || t("errors.somethingWentWrong"));
  };

  const {
    createSkynetUser,
    error: createError,
    loading: createLoading,
  } = useCreateSkynetUser({
    onCompleted: onCompletedCreateUser,
    onError: onErrorCreateUser,
  });

  const onCompletedUpdateUser = (result?: any) => {
    if (result?.rolesAndPermissions?.updateSkynetUser?.success) {
      setErrorMessage(null);
      onSuccess?.();
      onClose?.();
    } else {
      setErrorMessage(t("users.updateUserFailedMessage"));
    }
  };

  const onErrorUpdateUser = (error?: any) => {
    setErrorMessage(error?.message || t("errors.somethingWentWrong"));
  };

  const {
    updateSkynetUser,
    error: updateError,
    loading: updateLoading,
  } = useUpdateSkynetUser({
    onCompleted: onCompletedUpdateUser,
    onError: onErrorUpdateUser,
  });

  const { addMembers, loading: addMembersLoading } = useAddMembers({
    onCompleted: () => {},
    onError: (err) => console.error("Error adding user to group:", err),
  });

  const { removeMember, loading: removeMemberLoading } = useRemoveMember({
    onCompleted: () => {},
    onError: (err) => console.error("Error removing user from group:", err),
  });

  const error = createError || updateError;
  const loading =
    createLoading || updateLoading || addMembersLoading || removeMemberLoading;

  const isEmailValid = validateEmail(email.trim());
  const isValid =
    firstName.trim() !== "" && lastName.trim() !== "" && isEmailValid;

  const groupsChanged =
    originalGroupIds.length !== selectedGroups.length ||
    !originalGroupIds.every((id) => selectedGroups.includes(id));

  const isDirty = user
    ? firstName !== (user.firstName ?? "") ||
      lastName !== (user.lastName ?? "") ||
      email !== (user.email ?? "") ||
      amUserId.trim() !== "" ||
      groupsChanged
    : true;

  const onClickSave = async () => {
    try {
      if (user) {
        const addedGroupIds = selectedGroups.filter(
          (id) => !originalGroupIds.includes(id),
        );
        const removedMemberships = (user.groups ?? []).filter(
          (membership) => !selectedGroups.includes(membership.groupId),
        );

        const groupResults = await Promise.allSettled([
          ...addedGroupIds.map((groupId) =>
            addMembers({
              variables: {
                input: { groupId, skynetUserIds: [user.id] },
              },
            }),
          ),
          ...removedMemberships.map((membership) =>
            removeMember({
              variables: {
                input: { groupId: membership.groupId, memberId: membership.id },
              },
            }),
          ),
        ]);

        const groupsSucceeded = groupResults.every((result: any) => {
          if (result.status !== "fulfilled") return false;
          return (
            result.value?.data?.rolesAndPermissions?.addMembers?.success ??
            result.value?.data?.rolesAndPermissions?.removeMember?.success
          );
        });

        if (!groupsSucceeded) {
          setErrorMessage(t("users.updateUserFailedMessage"));
          return;
        }

        await updateSkynetUser({
          variables: {
            input: {
              skynetUserId: user.id,
              firstName,
              lastName,
              email,
              amUserId: amUserId || undefined,
            },
          },
        });
      } else {
        await createSkynetUser({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              groupIds: selectedGroups,
              amUserId: amUserId || undefined,
            },
          },
        });
      }
    } catch (err: any) {
      console.error("Error saving user:", err);
      setErrorMessage(err?.message || t("errors.somethingWentWrong"));
    }
  };

  const [deleteErrorMessage, setDeleteErrorMessage] = useState<
    string | undefined
  >(undefined);

  const onCompletedDeleteUser = (result?: any) => {
    if (result?.rolesAndPermissions?.deleteSkynetUser?.success) {
      setDeleteErrorMessage(undefined);
      onUserDeleted?.();
      onClose?.();
    } else {
      setDeleteErrorMessage(t("errors.userCouldNotBeDeleted"));
    }
  };

  const onErrorDeleteUser = (e?: any) => {
    setDeleteErrorMessage(e?.message || t("errors.userCouldNotBeDeleted"));
  };

  const { deleteSkynetUser, loading: deleteLoading } = useDeleteSkynetUser({
    onCompleted: onCompletedDeleteUser,
    onError: onErrorDeleteUser,
  });

  const handleDeleteUser = async () => {
    if (!user) return;
    try {
      await deleteSkynetUser({
        variables: {
          input: {
            skynetUserId: user.id,
          },
        },
      });
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  const groupOptions = (userGroups?.items ?? [])
    .filter((group) => !group.isSystemGroup)
    .map((group) => ({
      value: group.id,
      label: group.name,
    }));

  const selectedGroupObjects = (userGroups?.items ?? []).filter((group) =>
    selectedGroups.includes(group.id),
  );

  const previewPermissions: PermissionOverridePreview = {};
  PERMISSION_CATEGORIES.forEach(({ key }) => {
    const groupRead = selectedGroupObjects.flatMap(
      (group) => group.permissions?.[key]?.defaultRead ?? [],
    );
    const groupWrite = selectedGroupObjects.flatMap(
      (group) => group.permissions?.[key]?.defaultWrite ?? [],
    );
    const effective = user?.effectivePermissions?.[key];
    previewPermissions[key] = {
      read: Array.from(new Set([...groupRead, ...(effective?.read ?? [])])),
      write: Array.from(new Set([...groupWrite, ...(effective?.write ?? [])])),
    };
  });

  return (
    <div>
      <Subheading color={theme.colors.primary}>
        {user ? t("users.editUser") : t("users.createUser")}
      </Subheading>
      <HorizontalSpacer spacing={2} />
      <Row>
        <Col
          lg={5}
          style={{
            borderRight: `1px solid ${theme.colors.grey05}`,
            paddingRight: 24,
          }}
        >
          <Row>
            <Col lg={6}>
              <TextField
                label={t("users.firstName")}
                value={firstName}
                onChange={setFirstName}
                fullWidth
              />
            </Col>
            <Col lg={6}>
              <TextField
                label={t("users.lastName")}
                value={lastName}
                onChange={setLastName}
                fullWidth
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={1} />
          <Row>
            <Col lg={8}>
              <TextField
                label={t("common.email")}
                value={email}
                onChange={setEmail}
                fullWidth
                errorMessage={
                  email.trim() !== "" && !isEmailValid
                    ? t("common.invalidEmail")
                    : undefined
                }
              />
            </Col>
            <Col lg={4}>
              <TextField
                label={t("users.amId")}
                value={amUserId}
                onChange={setAmUserId}
                fullWidth
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={1} />
          <Row>
            <Col lg={12}>
              <BodySmall>{t("users.groups")}</BodySmall>
              <HorizontalSpacer spacing={0.2} />
              <MultiSelect
                options={groupOptions}
                selectedOptions={selectedGroups}
                onClickSelectAll={() =>
                  setSelectedGroups((prev) => {
                    const optionIds = new Set(
                      groupOptions.map((option) => option.value),
                    );
                    const allSelected = groupOptions.every((option) =>
                      prev.includes(option.value),
                    );
                    const withoutOptions = prev.filter(
                      (id) => !optionIds.has(id),
                    );
                    return allSelected
                      ? withoutOptions
                      : [
                          ...withoutOptions,
                          ...groupOptions.map((o) => o.value),
                        ];
                  })
                }
                onChange={setSelectedGroups}
                title={t("users.groups")}
                mode="picker"
              />
            </Col>
          </Row>
          {user && (
            <>
              <HorizontalSpacer spacing={2} />
              <Divider />
              <HorizontalSpacer spacing={2} />

              <div
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Row>
                  <Col lg={6}>
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("users.lastLogin")}
                    </BodySmall>
                  </Col>
                  <Col lg={6}>
                    <BodySmall>
                      {user.lastLoginAt
                        ? dayjs(user.lastLoginAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                    </BodySmall>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("random.created")}
                    </BodySmall>
                  </Col>
                  <Col lg={6}>
                    <BodySmall>
                      {user.createdAt
                        ? dayjs(user.createdAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                    </BodySmall>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("users.createdBy")}
                    </BodySmall>
                  </Col>
                  <Col lg={6}>
                    <BodySmall>saknas</BodySmall>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("users.lastModified")}
                    </BodySmall>
                  </Col>
                  <Col lg={6}>
                    <BodySmall>
                      {user.modifiedAt
                        ? dayjs(user.modifiedAt).format("YYYY-MM-DD HH:mm")
                        : "-"}
                    </BodySmall>
                  </Col>
                </Row>
                <Row>
                  <Col lg={6}>
                    <BodySmall color={theme.colors.textSecondary}>
                      {t("users.lastModifiedBy")}
                    </BodySmall>
                  </Col>
                  <Col lg={6}>
                    <BodySmall>saknas</BodySmall>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Col>
        <Col lg={7} style={{ paddingLeft: 24 }}>
          <div>
            <BodySmall>{t("users.permissionsDescription")}</BodySmall>
            <HorizontalSpacer spacing={1} />
            <UserPermissionsList
              effectivePermissions={previewPermissions}
              editable={false}
            />
          </div>
        </Col>
      </Row>

      <HorizontalSpacer spacing={2} />
      <Divider />
      <HorizontalSpacer spacing={2} />
      {(error || errorMessage) && (
        <Alert
          type="error"
          title={
            user
              ? t("users.updateUserFailedTitle")
              : t("users.createUserFailedTitle")
          }
          message={errorMessage}
        />
      )}
      {deleteErrorMessage && (
        <Alert
          type="error"
          title={t("errors.userCouldNotBeDeleted")}
          message={deleteErrorMessage}
        />
      )}
      <Row nogutter align="center" justify="between">
        <div>
          {user && (
            <DeleteModal
              deleteButtonType="outlinedButtonMedium"
              onClickDelete={handleDeleteUser}
              title={t("users.removeUser")}
              message={t("users.deleteUserMessage")}
              disabled={deleteLoading}
            />
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {onClose && (
            <DefaultButton title={t("common.cancel")} onClick={onClose} />
          )}
          <ActionButton
            color={theme.colors.primary}
            title={user ? t("common.save") : t("users.createUser")}
            onClick={onClickSave}
            loading={loading}
            disabled={!isValid || !isDirty}
          />
        </div>
      </Row>
    </div>
  );
};

export default ManageUserModalContent;
